import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { showToast } from "../../utils/method";
import { supplierInfoApi } from "../services/supplierInfoService";
import { selectCurrentProjectId } from "./projectDetailsSlice";

// Initial state
const initialState = {
	loading: false,
	error: null,
	supplierInfo: null,
};

// Async thunks
export const fetchSupplierInfoForInvoice = createAsyncThunk(
	"supplierInfo/fetchInvoice",
	async ({ invoiceId, forceRefresh = false }, { dispatch, rejectWithValue, getState }) => {
		const projectId = selectCurrentProjectId(getState());
		if (!projectId) return rejectWithValue("No project context");
		const queryResult = dispatch(
			supplierInfoApi.endpoints.getSupplierInfo.initiate({
				projectId,
				invoiceId,
				forceRefresh,
			}),
		);
		try {
			const response = await queryResult.unwrap();
			return response;
		} catch (error) {
			return rejectWithValue(
				error.message || "Failed to fetch supplier info sheet",
			);
		} finally {
			queryResult.unsubscribe();
		}
	},
);

export const saveSupplierInfo = createAsyncThunk(
	"supplierInfo/save",
	async ({ finalize }, { dispatch, getState, rejectWithValue }) => {
		const projectId = selectCurrentProjectId(getState());
		if (!projectId) return rejectWithValue("No project context");
		const state = getState();
		const { supplierInfo } = state.supplierInfo;
		const mutationResult = dispatch(
			supplierInfoApi.endpoints.saveSupplierInfo.initiate({
				projectId,
				invoiceId: supplierInfo.invoice?.id,
				supplierInfo,
				status: finalize ? "FINALIZED" : "DRAFT",
			}),
		);
		try {
			const response = await mutationResult.unwrap();
			return response;
		} catch (error) {
			return rejectWithValue(
				error.message || "Failed to save supplier information",
			);
		} finally {
			if (typeof mutationResult.reset === "function") {
				mutationResult.reset();
			}
		}
	},
);

// Add a new action to update the useCalculatedSecurity within the supplierInfo object
export const updateUseCalculatedSecurity = createAsyncThunk(
	"supplierInfo/updateSecurity",
	async ({ useCalculatedSecurity }, { getState }) => {
		const state = getState();
		return {
			...state.supplierInfo.supplierInfo,
			useCalculatedSecurity,
		};
	},
);

// Create the slice
const supplierInfoSlice = createSlice({
	name: "supplierInfo",
	initialState,
	reducers: {
		clearSupplierInfoState: (state) => {
			state.error = null;
			state.supplierInfo = null;
		},
		clearErrors: (state) => {
			state.error = null;
		},
		// Removed setUseCalculatedSecurity reducer
	},
	extraReducers: (builder) => {
		builder
			// Fetch invoice
			.addCase(fetchSupplierInfoForInvoice.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(fetchSupplierInfoForInvoice.fulfilled, (state, action) => {
				state.loading = false;
				state.supplierInfo = action.payload || {};
				// If useCalculatedSecurity isn't defined in the response, set a default
				if (
					state.supplierInfo &&
					state.supplierInfo.useCalculatedSecurity === undefined
				) {
					state.supplierInfo.useCalculatedSecurity = true;
				}
			})
			.addCase(fetchSupplierInfoForInvoice.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload;
			})

			// Handle the new updateUseCalculatedSecurity action
			.addCase(updateUseCalculatedSecurity.fulfilled, (state, action) => {
				state.supplierInfo = action.payload;
			})

			// Save supplier info
			.addCase(saveSupplierInfo.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(saveSupplierInfo.fulfilled, (state, action) => {
				state.loading = false;

				// Update the supplier info with the returned data
				if (action.payload) {
					state.supplierInfo = action.payload;
				}

				const message = action.payload.finalize
					? "supplier-info.finalized"
					: "supplier-info.saved";

				showToast(message, { type: "success" });
			})
			.addCase(saveSupplierInfo.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload;
				showToast("supplier-info.save-error", {
					type: "error",
				});
			});
	},
});

// Export actions and selectors
export const { clearSupplierInfoState, clearErrors } =
	supplierInfoSlice.actions;

// Updated selector to get useCalculatedSecurity from the supplierInfo object
export const selectUseCalculatedSecurity = (state) =>
	state.supplierInfo.supplierInfo?.useCalculatedSecurity ?? true;

export const selectSupplierInfoLoading = (state) => state.supplierInfo.loading;
export const selectSupplierInfoError = (state) => state.supplierInfo.error;
export const selectSupplierInfo = (state) => state.supplierInfo.supplierInfo;

export default supplierInfoSlice.reducer;
